import { useId, useState } from "react";
import type { RoleView } from "@starsach/shared-types";
import { useAccess, useCreateRole, useDeleteRole, useRoles, useUpdateRole } from "../../api/hooks";
import { useStepUp } from "../../auth/StepUpProvider";
import { Hint } from "../../components/Hint";
import { NotHeld } from "../../components/NotHeld";
import { Card, DataTable, Dialog, EmptyState, ErrorState, LoadingState, MutationError, RelativeTime, StatusPill, btnPrimary, btnSecondary, inputClass } from "../../components/ui";
import { RoleMatrix } from "./RoleMatrix";
import { orderRoles } from "./parts";

type Draft = { roleId?: string; name: string; description: string; permissions: Set<string>; readOnly: boolean };

/**
 * The organization's roles (ADR-0017 sections 2 and 6): the three
 * built-ins, read-only with "Copy to edit", and the organization's own,
 * each a name, a description and the matrix. Roles are the
 * organization's, not the environment's; the environment in the address
 * only says which set the caller's own permissions are read from.
 */
export function RolesSection({ environmentId }: { environmentId: string }) {
  const q = useRoles();
  const access = useAccess();
  const create = useCreateRole();
  const update = useUpdateRole();
  const remove = useDeleteRole();
  const { ensureFresh } = useStepUp();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [deleting, setDeleting] = useState<RoleView | null>(null);
  const canCreate = access.can(environmentId, "roles:create");
  const canEdit = access.can(environmentId, "roles:edit");
  const canDelete = access.can(environmentId, "roles:delete");

  if (q.isPending) return <LoadingState label="Loading roles" />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;

  const open = (r: RoleView, mode: "view" | "edit" | "copy") =>
    setDraft(
      mode === "copy"
        ? { name: `${r.name} (copy)`, description: r.description ?? "", permissions: new Set(r.permissions), readOnly: false }
        : { roleId: r.roleId, name: r.name, description: r.description ?? "", permissions: new Set(r.permissions), readOnly: mode === "view" },
    );
  const save = (d: Draft) => {
    const body = { name: d.name.trim(), ...(d.description.trim() ? { description: d.description.trim() } : {}), permissions: [...d.permissions].sort() };
    void ensureFresh().then(() => {
      if (d.roleId) update.mutate({ roleId: d.roleId, ...body }, { onSuccess: () => setDraft(null) });
      else create.mutate(body, { onSuccess: () => setDraft(null) });
    }, () => undefined);
  };

  return (
    <div className="space-y-4">
      <Card title={<>Roles<Hint id="access.roles" label="what a role is" /></>}>
        <p className="mb-3 text-sm text-neutral-600 dark:text-neutral-400">
          A role names what kind of thing its holder may do. Viewer, Admin and Manager are built in and cannot change;
          copy one to start your own. A change to a role applies to everyone holding it on their next request.
        </p>
        <MutationError of={remove} className="mb-3" />
        <DataTable
          caption="Roles in your organization"
          rows={orderRoles(q.data.roles)}
          rowKey={(r) => r.roleId}
          empty={<EmptyState title="No roles" />}
          columns={[
            { key: "name", header: "Role", render: (r) => <><span className="font-medium">{r.name}</span>{r.builtIn && <StatusPill status="built in" tone="neutral" />}</> },
            { key: "description", header: "", render: (r) => <span className="text-sm text-neutral-600 dark:text-neutral-400">{r.description ?? ""}</span> },
            { key: "count", header: "Permissions", numeric: true, render: (r) => r.permissions.length },
            { key: "held", header: "Held by", numeric: true, render: (r) => r.heldBy },
            // A built-in never changes; the API dates it from the epoch, which read as "20718 d ago" on the first walk.
            { key: "updated", header: "Changed", render: (r) => (r.builtIn ? <span className="text-neutral-500">never</span> : <RelativeTime iso={r.updatedAt} />) },
            {
              key: "actions",
              header: "",
              render: (r) => (
                <span className="flex flex-wrap gap-2">
                  {r.builtIn || !canEdit ? (
                    <button type="button" className="text-sm underline" onClick={() => open(r, "view")}>View</button>
                  ) : (
                    <button type="button" className="text-sm underline" onClick={() => open(r, "edit")}>Edit</button>
                  )}
                  {canCreate && <button type="button" className="text-sm underline" onClick={() => open(r, "copy")}>Copy to edit</button>}
                  {!r.builtIn && canDelete && (
                    <button type="button" className="text-sm text-red-700 underline dark:text-red-400" onClick={() => setDeleting(r)}>Delete</button>
                  )}
                </span>
              ),
            },
          ]}
        />
        <div className="mt-4 flex flex-wrap items-center gap-3">
          {canCreate ? (
            <button type="button" className={btnPrimary} onClick={() => setDraft({ name: "", description: "", permissions: new Set(), readOnly: false })}>
              New role
            </button>
          ) : (
            <NotHeld permission="roles:create" />
          )}
        </div>
      </Card>
      {draft && <RoleEditor draft={draft} onChange={setDraft} onSave={save} onClose={() => setDraft(null)} pending={create.isPending || update.isPending} error={create.isError ? create.error : update.isError ? update.error : undefined} />}
      {deleting && (
        <Dialog title={`Delete ${deleting.name}?`} onClose={() => setDeleting(null)}>
          <p className="text-sm">
            {deleting.heldBy === 0 ? "Nobody holds this role." : `${deleting.heldBy} membership${deleting.heldBy === 1 ? "" : "s"} or group${deleting.heldBy === 1 ? "" : "s"} name${deleting.heldBy === 1 ? "s" : ""} it; they lose what it granted at once.`}
          </p>
          <MutationError of={remove} className="mt-3" />
          <div className="mt-4 flex gap-2">
            <button type="button" className={btnPrimary} disabled={remove.isPending} onClick={() => void ensureFresh().then(() => remove.mutate({ roleId: deleting.roleId }, { onSuccess: () => setDeleting(null) }), () => undefined)}>
              {remove.isPending ? "Deleting…" : "Delete"}
            </button>
            <button type="button" className={btnSecondary} onClick={() => setDeleting(null)}>Cancel</button>
          </div>
        </Dialog>
      )}
    </div>
  );
}

function RoleEditor({ draft, onChange, onSave, onClose, pending, error }: { draft: Draft; onChange: (d: Draft) => void; onSave: (d: Draft) => void; onClose: () => void; pending: boolean; error: unknown }) {
  const id = useId();
  return (
    <Card title={draft.readOnly ? `${draft.name} (built in, read only)` : draft.roleId ? `Edit ${draft.name}` : "New role"}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!draft.readOnly) onSave(draft);
        }}
      >
        <div className="grid gap-3 md:grid-cols-2">
          <label htmlFor={`${id}-name`} className="text-sm">
            <span className="block font-medium">Name</span>
            <input id={`${id}-name`} className={inputClass} value={draft.name} maxLength={60} required readOnly={draft.readOnly} onChange={(e) => onChange({ ...draft, name: e.target.value })} />
          </label>
          <label htmlFor={`${id}-desc`} className="text-sm">
            <span className="block font-medium">Description</span>
            <input id={`${id}-desc`} className={inputClass} value={draft.description} maxLength={300} readOnly={draft.readOnly} onChange={(e) => onChange({ ...draft, description: e.target.value })} />
          </label>
        </div>
        <RoleMatrix permissions={draft.permissions} readOnly={draft.readOnly} onChange={(permissions) => onChange({ ...draft, permissions })} />
        {error !== undefined && <ErrorState error={error} lead="Couldn’t do that." />}
        <div className="flex gap-2">
          {!draft.readOnly && (
            <button type="submit" className={btnPrimary} disabled={pending || !draft.name.trim()}>
              {pending ? "Saving…" : draft.roleId ? "Save role" : "Create role"}
            </button>
          )}
          <button type="button" className={btnSecondary} onClick={onClose}>{draft.readOnly ? "Close" : "Cancel"}</button>
        </div>
      </form>
    </Card>
  );
}
